import * as React from 'react';
import { cn } from '../../../lib/utils';
import { SidebarProvider, useSidebar } from './sidebar';

/**
 * AppShell component with exact Hyperkit styling
 *
 * The main layout wrapper that orchestrates sidebar, header, and content.
 *
 * Hyperkit Specs:
 * - Sidebar: 260px/72px (expanded/collapsed)
 * - Header: 52px sticky top
 * - Content: Fills remaining space
 * - Transitions: 200ms ease
 */

const AppShellContext = React.createContext({
  darkMode: true,
  setDarkMode: () => {},
});

export function useAppShell() {
  const context = React.useContext(AppShellContext);
  if (!context) {
    throw new Error('useAppShell must be used within an AppShellProvider');
  }
  return context;
}

export function AppShellProvider({ children, defaultDarkMode = true }) {
  const [darkMode, setDarkMode] = React.useState(() => {
    if (typeof window === 'undefined') return defaultDarkMode;
    const saved = localStorage.getItem('trustgate-theme');
    return saved ? saved === 'dark' : defaultDarkMode;
  });

  const toggleDarkMode = React.useCallback(() => {
    setDarkMode((prev) => {
      const next = !prev;
      localStorage.setItem('trustgate-theme', next ? 'dark' : 'light');
      return next;
    });
  }, []);

  return (
    <AppShellContext.Provider value={{ darkMode, setDarkMode, toggleDarkMode }}>
      {children}
    </AppShellContext.Provider>
  );
}

export const AppShell = React.forwardRef(
  (
    {
      className,
      children,
      defaultCollapsed = false,
      defaultDarkMode = true,
      ...props
    },
    ref
  ) => {
    return (
      <AppShellProvider defaultDarkMode={defaultDarkMode}>
        <SidebarProvider defaultCollapsed={defaultCollapsed}>
          <AppShellInner ref={ref} className={className} {...props}>
            {children}
          </AppShellInner>
        </SidebarProvider>
      </AppShellProvider>
    );
  }
);
AppShell.displayName = 'AppShell';

const AppShellInner = React.forwardRef(
  ({ className, children, ...props }, ref) => {
    const { darkMode } = useAppShell();

    return (
      <div
        ref={ref}
        data-theme={darkMode ? 'dark' : 'light'}
        className={cn(
          'min-h-screen bg-grey-50 text-grey-900',
          'dark:bg-grey-950 dark:text-grey-50',
          'font-sans antialiased',
          className
        )}
        {...props}
      >
        {children}
      </div>
    );
  }
);
AppShellInner.displayName = 'AppShellInner';

export const AppShellMain = React.forwardRef(
  ({ className, children, noPadding = false, ...props }, ref) => {
    const { collapsed } = useSidebar();

    return (
      <div
        ref={ref}
        className={cn(
          'transition-all duration-200 ease-out flex flex-col min-h-screen',
          collapsed ? 'ml-[72px]' : 'ml-[260px]',
          className
        )}
        {...props}
      >
        {children}
      </div>
    );
  }
);
AppShellMain.displayName = 'AppShellMain';

export const AppShellContent = React.forwardRef(
  ({ className, children, noPadding = false, ...props }, ref) => {
    return (
      <main
        ref={ref}
        className={cn(
          'flex-1 flex flex-col min-h-0 overflow-hidden bg-grey-50 dark:bg-grey-950',
          !noPadding && 'p-4',
          className
        )}
        {...props}
      >
        {children}
      </main>
    );
  }
);
AppShellContent.displayName = 'AppShellContent';
